import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { SubscriptionApp, SubscriptionModel } from '../interfaces';
import { numberFormatted } from '../Formatters';
import { CHANGE_SUBSCRIPTION_APPS, GET_SUBSCRIPTION_APPS } from './subscriptions.graphql';
import { SubscriptionUsage } from './SubscriptionDialog';
import { ChipPicker } from './ChipPicker';

export function useSubscriptionApps() {
  const { data, loading, error } = useQuery<{ apps: SubscriptionApp[] }>(GET_SUBSCRIPTION_APPS);
  return { apps: data?.apps, loading, error };
}

interface Props {
  subscription: SubscriptionModel;
  usage?: SubscriptionUsage;
  onClose(): void;
}

export function SubscriptionAppsDialog({ subscription, usage, onClose }: Props) {
  const [chosen, setChosen] = useState<string[]>(subscription.appIds ?? []);
  const [error, setError] = useState<string | null>(null);
  const { apps, loading, error: appsError } = useSubscriptionApps();
  const [change, { loading: saving }] = useMutation(CHANGE_SUBSCRIPTION_APPS, { refetchQueries: ['GetProductChanges'] });
  const inUse = !!usage && (usage.licenses > 0 || usage.users > 0);
  // Only the standalone apps the apps service lists can be chosen, so any other the subscription lists drops out here.
  const value = (apps ?? []).filter((x) => chosen.includes(x.appId));
  const dropped = apps ? (subscription.appIds ?? []).filter((id) => !apps.some((x) => x.appId === id)).length : 0;

  const save = async () => {
    setError(null);
    try {
      await change({ variables: { input: { subscriptionId: subscription.bksId, appIds: value.map((x) => x.appId) } } });
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'Kunne ikke lagre appene.');
    }
  };

  return (
    // Held at the top, so the dialog grows downwards as apps are chosen.
    <Dialog open fullWidth maxWidth="sm" onClose={onClose} sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}>
      <DialogTitle>Apper i {subscription.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <ChipPicker
            label="Apper"
            placeholder="Søk på navn"
            options={apps ?? []}
            value={value}
            onChange={(picked) => setChosen(picked.map((x) => x.appId))}
            getKey={(x) => x.appId}
            getLabel={(x) => x.name}
            groupBy={(x) => x.category || 'Andre'}
            loading={loading}
            error={!!appsError}
            helperText={
              appsError ? 'Fikk ikke hentet appene fra apps-tjenesten.' : 'Apper i anvisninger følger tilgangen til anvisningen og velges ikke her.'
            }
          />

          <Typography variant="body2" color="text.secondary">
            {value.length === 0 ? 'Gir ingen apper.' : `Gir ${value.length === 1 ? '1 app' : `${value.length} apper`}.`}
          </Typography>

          {dropped > 0 && (
            <Alert severity="info">
              {dropped === 1 ? 'Én app' : `${dropped} apper`} på abonnementet er ikke selvstendig eller finnes ikke i apps-tjenesten, og fjernes når
              du lagrer.
            </Alert>
          )}
          {inUse && (
            <Alert severity="warning">
              Endringen gjelder med en gang for {numberFormatted(usage.licenses) ?? 0} lisenser og {numberFormatted(usage.users) ?? 0} brukere.
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={saving} onClick={onClose}>
          Avbryt
        </Button>
        <Button variant="contained" disabled={!apps} loading={saving} onClick={save}>
          Lagre
        </Button>
      </DialogActions>
    </Dialog>
  );
}
